import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ulid } from 'ulid';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { TariffViewModel, CreateTariffMutation, CreateTariffMutationVariables } from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import { AutocompleteCreateFormProps } from './autocomplete-create-form';

import { TariffForm } from '../../pages/tariffs/tariff-form';
import { buildCreateTariffInput, type TariffFormSubmitData } from '../../pages/tariffs/tariff-input';
import { TARIFF_CREATE_MUTATION } from '../../pages/tariffs/tariffs.gql';

export function TariffCreateForm({ onSuccess, onCancel }: AutocompleteCreateFormProps<TariffViewModel>) {
	const [submissionError, setSubmissionError] = useState<string | null>(null);
	const idempotentTariffId = useMemo(() => ulid(), []);

	const [createTariff, { loading: isSubmitting }] = useMutation<
		CreateTariffMutation,
		CreateTariffMutationVariables
	>(TARIFF_CREATE_MUTATION, {
		// Prevent cache interactions that might trigger refreshes
		fetchPolicy: 'no-cache'
	});

	const handleSubmit = async (formData: TariffFormSubmitData) => {
		setSubmissionError(null);

		const payload = buildCreateTariffInput(formData, idempotentTariffId);

		try {
			const { data } = await createTariff({
				variables: { input: payload }
			});

			const message = extractCheckResultError(data?.createTariff);

			if (message) {
				setSubmissionError(message);
				return;
			}

		// Skip success toast in autocomplete context to prevent navigation issues
		// The autocomplete selection will provide user feedback

		// Create a minimal TariffViewModel object to pass back
		// Note: facility and other fields will be populated when the list refreshes
		const newTariff = {
			tariffId: idempotentTariffId,
			name: formData.name,
			tariffTypeEnum: formData.tariffTypeEnum,
		} as TariffViewModel;

		onSuccess(newTariff);
		} catch (error) {
			const fallbackMessage =
				error instanceof Error
					? error.message
					: 'Unexpected error while creating tariff.';
			setSubmissionError(fallbackMessage);
		}
	};

	return (
		<div className="space-y-4">
			{submissionError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to create tariff</AlertTitle>
					<AlertDescription>{submissionError}</AlertDescription>
				</Alert>
			)}

			<TariffForm
				mode="create"
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Create tariff"
				onBack={onCancel}
			/>
		</div>
	);
}
